import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/** Skim filter: pointer must dwell this long before MegaMenuHoverEvent fires. */
const HOVER_COMMIT_DELAY_MS = 150;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverDispatchTimer;
  #desktopOpenTimer;
  #desktopCloseTimer;
  #hoverPendingItem = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    document.addEventListener('pointermove', this.#onDesktopHoverMove, { passive: true });

    window.addEventListener('resize', this.#resizeListener);

    this.overflowMenu?.addEventListener(
      'pointerleave',
      this.#overflowSubmenuListener
    );

    /*
     * Desktop click-to-open.
     *
     * Capture mode makes our handler run before other click
     * handlers attached to the menu.
     */
    this.addEventListener(
      'click',
      this.#onDesktopClick,
      true
    );

    /*
     * Prevent the first mouse interaction from focusing the
     * parent menu link. The click itself is still handled below.
     */
    this.addEventListener(
      'pointerdown',
      this.#onDesktopPointerDown,
      true
    );

    document.addEventListener(
      'click',
      this.#onDocumentClick,
      true
    );

    document.addEventListener(
      'keydown',
      this.#onKeyDown
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('pointermove', this.#onDesktopHoverMove);
    clearTimeout(this.#desktopOpenTimer);
    clearTimeout(this.#desktopCloseTimer);

    window.removeEventListener(
      'resize',
      this.#resizeListener
    );

    document.body.removeEventListener(
      'pointermove',
      this.#onPointerMove
    );

    if (this.#state.activeItem) {
      this.#stopPointerTracking(
        this.#state.activeItem
      );
    }

    this.overflowMenu?.removeEventListener(
      'pointerleave',
      this.#overflowSubmenuListener
    );

    this.#cleanupMutationObserver();

    this.removeEventListener(
      'click',
      this.#onDesktopClick,
      true
    );

    this.removeEventListener(
      'pointerdown',
      this.#onDesktopPointerDown,
      true
    );

    document.removeEventListener(
      'click',
      this.#onDocumentClick,
      true
    );

    document.removeEventListener(
      'keydown',
      this.#onKeyDown
    );

    clearTimeout(
      this.#hoverDispatchTimer
    );

    this.#hoverDispatchTimer = undefined;
  }

  /**
   * Whether this menu is currently being used on desktop.
   */
  #isDesktop() {
    return window.matchMedia(
      '(min-width: 750px)'
    ).matches;
  }

  /**
   * Debounced resize event listener to recalculate menu style.
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  /**
   * On desktop, pointer movement should not close the menu.
   * Mobile keeps Horizon's original behavior.
   */
  #overflowSubmenuListener = () => {
    if (this.#isDesktop()) return;

    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   *
   * @type {{ x: number, y: number }}
   */
  #lastPointer = {
    x: 0,
    y: 0,
  };

  /**
   * Update the safety box idle state on the active menu item.
   *
   * This is retained for mobile/Horizon behavior only.
   *
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    if (this.#isDesktop()) return;

    const activeLink =
      this.#state.activeItem;

    if (!activeLink) return;

    this.#lastPointer.x =
      event.clientX;

    this.#lastPointer.y =
      event.clientY;

    const moving =
      Math.abs(event.movementX) >= 1 ||
      Math.abs(event.movementY) >= 1;

    activeLink.dataset.safetyBox =
      `${moving}`;

    clearTimeout(
      this.#pointerIdleTimer
    );

    if (moving) {
      this.#pointerIdleTimer =
        setTimeout(() => {
          if (this.#state.activeItem) {
            this.#state.activeItem.dataset.safetyBox =
              'false';

            this.#reconcilePointerTarget();
          }
        }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and
   * trigger activation if so.
   *
   * This remains available for mobile/Horizon behavior.
   */
  #reconcilePointerTarget() {
    if (this.#isDesktop()) return;

    const {
      x,
      y,
    } = this.#lastPointer;

    requestAnimationFrame(() => {
      const target =
        document.elementFromPoint(
          x,
          y
        );

      if (!target) return;

      const listItem =
        target.closest(
          '.menu-list__list-item'
        );

      if (
        listItem &&
        !listItem.contains(
          this.#state.activeItem
        )
      ) {
        listItem.dispatchEvent(
          new PointerEvent(
            'pointerenter',
            {
              bubbles: false,
            }
          )
        );
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box
   * on the newly active item.
   *
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(
    item,
    previousItem
  ) {
    if (this.#isDesktop()) return;

    if (previousItem) {
      this.#stopPointerTracking(
        previousItem
      );
    } else {
      document.body.addEventListener(
        'pointermove',
        this.#onPointerMove
      );
    }

    const rect =
      item.getBoundingClientRect();

    const isOverlap =
      this.headerComponent?.hasAttribute(
        'data-submenu-overlap-bottom-row'
      );

    const boundary =
      isOverlap
        ? this.headerComponent?.querySelector(
            '.header__row--top'
          )
        : this.headerComponent;

    item.style.setProperty(
      '--box-height',
      `${
        (boundary?.getBoundingClientRect().bottom ?? 0) -
        rect.top
      }px`
    );
  }

  /**
   * Stop pointer tracking and remove all safety box properties.
   *
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    window.clearTimeout(
      this.#pointerIdleTimer
    );

    this.#pointerIdleTimer =
      undefined;

    item.style.removeProperty(
      '--box-height'
    );

    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu.
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (
      this.refs.overflowMenu
        ?.shadowRoot
        ?.querySelector(
          '[part="overflow"]'
        )
    );
  }

  /**
   * Whether the overflow list is hovered.
   *
   * @returns {boolean}
   */
  get overflowListHovered() {
    return (
      this.refs.overflowMenu
        ?.shadowRoot
        ?.querySelector(
          '[part="overflow-list"]'
        )
        ?.matches(':hover') ?? false
    );
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (
      this.closest(
        'header-component'
      )
    );
  }

  /**
   * Prevent the first desktop mouse interaction from
   * focusing the parent link.
   *
   * We do this only for links that actually have a submenu.
   * Normal navigation links are untouched.
   *
   * @param {PointerEvent} event
   */
  #onDesktopPointerDown = (event) => {
    if (!this.#isDesktop()) return;

    if (
      !(event.target instanceof Element)
    ) {
      return;
    }

    const link =
      event.target.closest(
        '.menu-list__link'
      );

    if (
      !link ||
      !this.contains(link)
    ) {
      return;
    }

    const listItem =
      link.closest(
        '.menu-list__list-item'
      );

    if (!listItem) return;

    const submenu =
      listItem.querySelector(
        ':scope > .menu-list__submenu'
      );

    if (!submenu) return;

    /*
     * Prevent the browser from giving focus to the
     * submenu parent before our click handler runs.
     */
    event.preventDefault();
  };

  /**
   * Desktop click-to-open handler.
   *
   * Parent links with submenus are treated as toggles.
   * Normal links continue to navigate normally.
   *
   * @param {MouseEvent} event
   */
#onDesktopClick = (event) => {
  if (!this.#isDesktop()) return;

  if (!(event.target instanceof Element)) return;

  const link = event.target.closest('.menu-list__link');

  if (!link || !this.contains(link)) return;

  const listItem = link.closest('.menu-list__list-item');

  if (!listItem) return;

  const item = findMenuItem(listItem);

  if (!item) return;

  const submenu = findSubmenu(item);

  /*
   * Normal links continue to work normally.
   */
  if (!submenu) return;

  event.preventDefault();
  event.stopPropagation();

  this.#openDesktopMenuItem(item);
};


  /**
   * Preserve the existing click toggle behavior, also callable after a true
   * desktop mouse hover. This deliberately does not modify the mobile drawer.
   */
  #openDesktopMenuItem(item) {
    const submenu = findSubmenu(item);
    if (!submenu) return;
  /*
   * If this submenu is already open, close it.
   */
  if (this.#state.activeItem === item) {
    this.#deactivate(item, true);
    return;
  }

  /*
   * Close any other open desktop submenu first.
   */
  if (this.#state.activeItem) {
    this.#deactivate(
      this.#state.activeItem,
      true
    );
  }

  /*
   * Make this item the active desktop menu item
   * without calling Horizon's activate() method.
   */
  this.#positionDesktopSubmenu(item);
  this.#state.activeItem = item;

  this.dataset.overflowExpanded = 'false';

  this.ariaExpanded = 'true';
  item.ariaExpanded = 'true';

  /*
   * Tell Horizon that the submenu is active so that
   * content-visibility does not hide its contents.
   */
  submenu.dataset.active = '';

  /*
   * Make sure the submenu is visible immediately.
   */
  this.style.setProperty(
    '--submenu-opacity',
    '1'
  );

  /*
   * Measure the submenu after it has been activated.
   */
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (
        this.#state.activeItem !== item
      ) {
        return;
      }

      const submenuHeight =
        submenu.classList.contains('menu-list__submenu--compact') ? 0 : (submenu.offsetHeight || 0);

      const headerVisibleHeight =
        this.#getHeaderVisibleHeight();

      this.headerComponent?.style.setProperty(
        '--submenu-height',
        `${submenuHeight}px`
      );

      this.#setFullOpenHeaderHeight(
        submenuHeight,
        headerVisibleHeight
      );
    });
  });

  /*
   * Notify the rest of Horizon that a mega menu
   * has opened.
   */
  this.dispatchEvent(
    new MegaMenuHoverEvent()
  );
  }

  #positionDesktopSubmenu(item) {
    const submenu = findSubmenu(item);
    if (!submenu?.classList.contains('menu-list__submenu--compact')) return;
    const parentBounds = this.getBoundingClientRect();
    const triggerBounds = item.getBoundingClientRect();
    const width = Math.min(320, window.innerWidth - 24);
    const limit = Math.max(0, window.innerWidth - 12 - width - parentBounds.left);
    const x = Math.max(0, Math.min(triggerBounds.left - parentBounds.left, limit));
    submenu.style.setProperty('--pl-dropdown-left', `${Math.round(x)}px`);
  }

  /** A geometric hit test avoids invisible submenu overlays blocking upward hover. */
  #onDesktopHoverMove = (event) => {
    if (!this.#isDesktop() || event.pointerType !== 'mouse' ||
        !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    const x = event.clientX;
    const y = event.clientY;
    let link = null;

    for (const candidate of this.querySelectorAll(
      '.menu-list__list-item:not([slot="overflow"]) > .menu-list__link'
    )) {
      if (candidate.parentElement?.getAttribute('aria-hidden') === 'true') continue;
      const rect = candidate.getBoundingClientRect();
      if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
        link = candidate;
        break;
      }
    }

    if (link) {
      clearTimeout(this.#desktopCloseTimer);
      this.#desktopCloseTimer = undefined;
      if (findSubmenu(link)) {
        if (this.#state.activeItem === link) {
          clearTimeout(this.#desktopOpenTimer);
          this.#desktopOpenTimer = undefined;
          this.#hoverPendingItem = null;
          return;
        }
        if (this.#hoverPendingItem !== link) {
          clearTimeout(this.#desktopOpenTimer);
          this.#hoverPendingItem = link;
          this.#desktopOpenTimer = setTimeout(() => {
            const pending = this.#hoverPendingItem;
            this.#hoverPendingItem = null;
            this.#desktopOpenTimer = undefined;
            if (pending === link && this.isConnected && this.#isDesktop()) {
              this.#openDesktopMenuItem(link);
            }
          }, 100);
        }
      } else {
        clearTimeout(this.#desktopOpenTimer);
        this.#desktopOpenTimer = undefined;
        this.#hoverPendingItem = null;
        this.#scheduleDesktopClose();
      }
      return;
    }

    clearTimeout(this.#desktopOpenTimer);
    this.#desktopOpenTimer = undefined;
    this.#hoverPendingItem = null;

    const panel = findSubmenu(this.#state.activeItem);
    if (panel) {
      const rect = panel.getBoundingClientRect();
      if (x >= rect.left - 2 && x <= rect.right + 2 &&
          y >= rect.top - 3 && y <= rect.bottom + 3) {
        clearTimeout(this.#desktopCloseTimer);
        this.#desktopCloseTimer = undefined;
        return;
      }
    }
    this.#scheduleDesktopClose();
  };

  #scheduleDesktopClose() {
    if (!this.#state.activeItem || this.#desktopCloseTimer) return;
    this.#desktopCloseTimer = setTimeout(() => {
      this.#desktopCloseTimer = undefined;
      if (this.#state.activeItem) this.#deactivate(this.#state.activeItem, true);
    }, 160);
  }

  /**
   * Close the desktop menu when clicking outside.
   *
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    if (!this.#isDesktop()) return;

    if (
      event.target instanceof Node &&
      this.contains(event.target)
    ) {
      return;
    }

    if (
      this.#state.activeItem
    ) {
      this.#deactivate(
        this.#state.activeItem,
        true
      );
    }
  };

  /**
   * Close the desktop menu with Escape.
   *
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (!this.#isDesktop()) return;

    if (
      event.key !== 'Escape'
    ) {
      return;
    }

    if (
      !this.#state.activeItem
    ) {
      return;
    }

    event.preventDefault();

    this.#deactivate(
      this.#state.activeItem,
      true
    );
  };

  /**
   * Activate the selected menu item immediately.
   *
   * Desktop:
   *   only click is allowed to activate.
   *
   * Mobile:
   *   Horizon's original pointer/focus behavior remains.
   *
   * @param {{ target: EventTarget | null, type?: string }} event
   */
  activate = (event) => {
    if (
      !(event.target instanceof Element) ||
      !this.headerComponent
    ) {
      return;
    }

    /*
     * Desktop click-to-open.
     *
     * Ignore pointerenter and focus activation entirely.
     */
    if (
      this.#isDesktop() &&
      event.type !== 'click'
    ) {
      return;
    }

    const item =
      findMenuItem(
        event.target
      );

    if (
      !item ||
      item === this.#state.activeItem
    ) {
      return;
    }

    const isDefaultSlot =
      event.target.slot === '';

    this.dataset.overflowExpanded =
      (!isDefaultSlot).toString();

    const previouslyActiveItem =
      this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded =
        'false';
    }

    this.#state.activeItem =
      item;

    this.ariaExpanded =
      'true';

    item.ariaExpanded =
      'true';

    let submenu =
      findSubmenu(item);

    const hasSubmenu =
      Boolean(submenu);

    if (
      !hasSubmenu &&
      !isDefaultSlot
    ) {
      submenu =
        this.overflowMenu;
    }

    if (submenu) {
      clearTimeout(
        this.#hoverDispatchTimer
      );

      this.#hoverDispatchTimer =
        undefined;

      const committedItem =
        item;

      /*
       * Desktop click opens immediately.
       */
      if (
        this.#isDesktop() &&
        event.type === 'click'
      ) {
        this.dispatchEvent(
          new MegaMenuHoverEvent()
        );
      } else if (
        event instanceof FocusEvent
      ) {
        this.dispatchEvent(
          new MegaMenuHoverEvent()
        );
      } else {
        this.#hoverDispatchTimer =
          setTimeout(() => {
            this.#hoverDispatchTimer =
              undefined;

            if (
              this.#state.activeItem ===
              committedItem
            ) {
              this.dispatchEvent(
                new MegaMenuHoverEvent()
              );
            }
          }, HOVER_COMMIT_DELAY_MS);
      }

      /*
       * Mark submenu active for content visibility.
       */
      submenu.dataset.active =
        '';

      /*
       * Clean up any previous observer.
       */
      this.#cleanupMutationObserver();

      /*
       * Monitor submenu DOM changes so its height
       * is calculated correctly.
       */
      this.#submenuMutationObserver =
        new MutationObserver(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (
                submenu.offsetHeight > 0
              ) {
                this.headerComponent?.style.setProperty(
                  '--submenu-height',
                  `${submenu.offsetHeight}px`
                );

                this.#cleanupMutationObserver();
              }
            });
          });
        });

      this.#submenuMutationObserver.observe(
        submenu,
        {
          childList: true,
          subtree: true,
        }
      );

      /*
       * Auto-disconnect observer.
       */
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight =
      submenu?.offsetHeight || 0;

    /*
     * Overflow menu sizing.
     */
    if (!isDefaultSlot) {
      const overflowListHeight =
        this.#getOverflowListLinksHeight();

      if (hasSubmenu) {
        const overflowHeight =
          this.overflowMenu
            ?.offsetHeight || 0;

        finalHeight =
          Math.max(
            overflowHeight,
            overflowListHeight
          );
      } else {
        finalHeight =
          overflowListHeight;
      }
    }

    /*
     * Nothing to open.
     */
    if (!submenu) {
      finalHeight = 0;
    }

    const headerVisibleHeight =
      this.#getHeaderVisibleHeight();

    this.headerComponent.style.setProperty(
      '--submenu-height',
      `${finalHeight}px`
    );

    this.#setFullOpenHeaderHeight(
      finalHeight,
      headerVisibleHeight
    );

    this.style.setProperty(
      '--submenu-opacity',
      '1'
    );

    /*
     * Desktop click mode does not use pointer tracking.
     * Mobile continues using Horizon's original system.
     */
    if (
      this.#isDesktop() &&
      event.type === 'click'
    ) {
      if (
        previouslyActiveItem
      ) {
        this.#stopPointerTracking(
          previouslyActiveItem
        );
      }

      document.body.removeEventListener(
        'pointermove',
        this.#onPointerMove
      );
    } else {
      this.#startPointerTracking(
        item,
        previouslyActiveItem
      );
    }
  };

  /**
   * Deactivate the active item.
   *
   * Desktop:
   *   hover/focus does not close it.
   *
   * Mobile:
   *   Horizon's original behavior remains.
   *
   * @param {Event} event
   */
  deactivate(event) {
    if (
      !(event.target instanceof Element)
    ) {
      return;
    }

    /*
     * Desktop menus stay open until:
     *
     * - the parent is clicked again
     * - the user clicks outside
     * - Escape is pressed
     */
    if (this.#isDesktop()) {
      return;
    }

    const menu =
      findSubmenu(
        this.#state.activeItem
      );

    const isMovingWithinMenu =
      event.relatedTarget instanceof Node &&
      menu?.contains(
        document.activeElement
      );

    const isMovingToSubmenu =
      event.relatedTarget instanceof Node &&
      event.type === 'blur' &&
      menu?.contains(
        event.relatedTarget
      );

    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node &&
      event.relatedTarget.parentElement?.matches(
        '[slot="overflow"]'
      );

    if (
      isMovingWithinMenu ||
      isMovingToOverflowMenu ||
      isMovingToSubmenu
    ) {
      if (
        this.#state.activeItem
      ) {
        this.#stopPointerTracking(
          this.#state.activeItem
        );
      }

      return;
    }

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately.
   *
   * @param {HTMLElement | null} [item]
   * @param {boolean} [force]
   */
  #deactivate = (
    item = this.#state.activeItem,
    force = false
  ) => {
    if (
      !item ||
      item !== this.#state.activeItem
    ) {
      return;
    }

    /*
     * Preserve Horizon's overflow behavior on mobile.
     */
    if (
      !force &&
      (
        this.overflowListHovered ||
        this.overflowMenu?.matches(':hover')
      )
    ) {
      return;
    }

    clearTimeout(
      this.#hoverDispatchTimer
    );

    this.#hoverDispatchTimer =
      undefined;

    this.headerComponent?.style.setProperty(
      '--submenu-height',
      '0px'
    );

    this.#setFullOpenHeaderHeight(
      0,
      0
    );

    this.style.setProperty(
      '--submenu-opacity',
      '0'
    );

    this.dataset.overflowExpanded =
      'false';

    const submenu =
      findSubmenu(item);

    document.body.removeEventListener(
      'pointermove',
      this.#onPointerMove
    );

    this.#stopPointerTracking(
      item
    );

    this.#state.activeItem =
      null;

    this.ariaExpanded =
      'false';

    item.ariaExpanded =
      'false';

    /*
     * Remove active state from submenu
     * after the close animation.
     */
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks =
      this.overflowMenu
        ?.querySelector('slot')
        ?.assignedElements();

    if (!slottedMenuLinks) {
      return (
        this.overflowMenu
          ?.offsetHeight || 0
      );
    }

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach(
        (link) => {
          const submenu =
            /** @type {HTMLElement | null} */ (
              link.querySelector(
                '[ref="submenu[]"]'
              )
            );

          if (submenu) {
            cb(submenu);
          }
        }
      );
    };

    mapSubmenus(
      (submenu) => {
        submenu.style.setProperty(
          'display',
          'none'
        );
      }
    );

    const height =
      this.overflowMenu
        ?.offsetHeight || 0;

    mapSubmenus(
      (submenu) => {
        submenu.style.removeProperty(
          'display'
        );
      }
    );

    return height;
  }

  /**
   * Read the visible header height before submenu
   * height writes invalidate layout.
   *
   * @returns {number}
   */
  #getHeaderVisibleHeight() {
    if (!this.headerComponent) {
      return 0;
    }

    const isOverlapSituation =
      this.headerComponent.hasAttribute(
        'data-submenu-overlap-bottom-row'
      );

    return (
      isOverlapSituation &&
      this.headerComponent.offsetHeight > 0
    )
      ? (
          /** @type {HTMLElement | null} */ (
            this.headerComponent.querySelector(
              '.header__row--top'
            )
          )?.offsetHeight ?? 0
        )
      : this.headerComponent.offsetHeight;
  }

  /**
   * Calculate and set the full open header height.
   *
   * @param {number} submenuHeight
   * @param {number} headerVisibleHeight
   */
  #setFullOpenHeaderHeight(
    submenuHeight,
    headerVisibleHeight
  ) {
    if (!this.headerComponent) {
      return;
    }

    const nothingToOpen =
      submenuHeight === 0;

    const fullOpenHeaderHeight =
      nothingToOpen
        ? 0
        : submenuHeight +
          headerVisibleHeight;

    this.headerComponent?.style.setProperty(
      '--full-open-header-height',
      `${fullOpenHeaderHeight}px`
    );
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images =
      this.querySelectorAll(
        'img[loading="lazy"]'
      );

    images?.forEach(
      (image) => {
        image.removeAttribute(
          'loading'
        );
      }
    );
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();

    this.#submenuMutationObserver =
      null;
  }
}

if (
  !customElements.get('header-menu')
) {
  customElements.define(
    'header-menu',
    HeaderMenu
  );
}

/**
 * Find the closest menu item.
 *
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (
    !(element instanceof Element)
  ) {
    return null;
  }

  if (
    element.matches(
      '[slot="more"]'
    )
  ) {
    /*
     * Select the first overflowing menu item
     * when interacting with the More item.
     */
    return findMenuItem(
      element.parentElement?.querySelector(
        '[slot="overflow"]'
      )
    );
  }

  return element.querySelector(
    '[ref="menuitem"]'
  );
}

/**
 * Find the closest submenu.
 *
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu =
    element?.parentElement?.querySelector(
      '[ref="submenu[]"]'
    );

  return submenu instanceof HTMLElement
    ? submenu
    : null;
}